'use client'

import { useEffect, useMemo } from 'react'
import { usePathname } from 'next/navigation'
import { mergeBrandChannels, sumUnread } from '@/components/layout/AppRail/brandChannels'
import { setAppBadgeCount } from '@/lib/platform/badge'
import { APP_NAME } from '@/lib/pwa'
import { useChannelsQuery } from '@/hooks/useChannelsQuery'
import { useNotificationPrefs } from '@/hooks/notifications/useNotificationPrefs'
import { useNotificationsOverview } from '@/hooks/notifications/useNotificationsQuery'
import { useAuth } from '@/providers/AuthContext'

/** `(3) OmniChannel`. Puro — o teste do formato não precisa montar hook. */
export function formatAppTitle(count: number): string {
  return count > 0 ? `(${count}) ${APP_NAME}` : APP_NAME
}

/**
 * Leva o total de não lidas para fora da janela: título e badge do ícone.
 *
 * **Isto é estado, não notificação** (I4): não pede permissão, não tem pref de
 * desligar, e o número é o mesmo que o rail já mostra (`BrandMenu`).
 *
 * O badge **não existe no Chrome Android nem em Linux** (P4) — lá o hook é
 * no-op e o título é o que cobre. Título funciona em toda plataforma enquanto a
 * janela existir, e na janela instalada ele é o nome no alt-tab e na taskbar.
 *
 * `pathname` entra nas deps porque o App Router reescreve o `<title>` a partir do
 * `metadata` a cada navegação, e o `metadata` de `app/layout.tsx` é estático: sem
 * isso o contador some na primeira troca de rota e só volta na mensagem seguinte.
 *
 * **Os dois efeitos limpam no unmount, e o do badge é o que importa de verdade.**
 * O badge é estado do SISTEMA OPERACIONAL, não da página: ele sobrevive à troca
 * de documento que o `logout` faz (`AuthContext` → `window.location.assign`), a
 * fechar o app e a reabri-lo. Sem a limpeza, o ícone do app instalado fica
 * marcando as não lidas do operador que saiu — e o próximo que logar na mesma
 * máquina vê a contagem alheia até o número dele mudar.
 */
export function useUnreadSignals(): void {
  const { user } = useAuth()
  const { data } = useChannelsQuery()
  const pathname = usePathname()
  // A vista reativa: o hook ja escuta `PREFS_CHANGE_EVENT`, entao trocar a fonte
  // na tela de preferencias refaz o numero na hora, sem recarregar.
  const { prefs } = useNotificationPrefs()
  const { data: overview } = useNotificationsOverview()

  const mensagens = useMemo(
    () => sumUnread(mergeBrandChannels(user?.channels, data)),
    [user?.channels, data],
  )

  // Overview ainda nao resolvido cai em 0, nunca no total de mensagens: mostrar
  // a fonte errada e pior que mostrar numero nenhum.
  const total = prefs.badgeSource === 'notifications' ? (overview?.counts.new ?? 0) : mensagens

  useEffect(() => {
    document.title = formatAppTitle(total)
    return () => {
      document.title = APP_NAME
    }
  }, [total, pathname])

  useEffect(() => {
    void setAppBadgeCount(total)
    return () => {
      void setAppBadgeCount(0)
    }
  }, [total])
}
